import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { fetchPaginatedImages } from '../services/api';
import { adaptPublicImage } from '../services/publicRecords';
import { formatDateYmd, getYearMonth } from '../utils/time';

export type BrandVariant = 'a' | 'b';
interface BrandDesignValue { variant: BrandVariant }
const BrandDesignContext = createContext<BrandDesignValue | null>(null);
const CACHE_TTL = 15 * 60 * 1000;

function variantForToday(dates: string[], today: string): BrandVariant {
  const publishedDays = dates.filter(date => date <= today).length;
  return publishedDays % 2 === 1 ? 'a' : 'b';
}

async function fetchMonthDates(year: string, month: string): Promise<string[]> {
  const monthKey = `${year}-${month}`;
  const cacheKey = `brand-update-days:${monthKey}`;
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    if (cached && Date.now() - cached.savedAt < CACHE_TTL && Array.isArray(cached.dates)) return cached.dates;
  } catch {}

  const dates = new Set<string>();
  let page = 1;
  let totalPages = 1;
  do {
    const result = await fetchPaginatedImages('month', { y: year, m: month, page });
    result.items.forEach(image => {
      const date = image.published_at ? formatDateYmd(image.published_at) : null;
      if (adaptPublicImage(image) && date?.startsWith(`${monthKey}-`)) dates.add(date);
    });
    totalPages = result.pagination.total_pages;
    page += 1;
  } while (page <= totalPages);

  const sortedDates = Array.from(dates).sort();
  try { localStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), dates: sortedDates })); } catch {}
  return sortedDates;
}

export const BrandDesignProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [today, setToday] = useState(() => formatDateYmd(Date.now()) || new Date().toISOString().slice(0, 10));
  useEffect(() => {
    const interval = window.setInterval(() => setToday(formatDateYmd(Date.now()) || new Date().toISOString().slice(0, 10)), 60_000);
    return () => window.clearInterval(interval);
  }, []);
  const { year, month } = getYearMonth(Date.now()) || { year: today.slice(0, 4), month: today.slice(5, 7) };
  const monthKey = `${year}-${month}`;
  const [variant, setVariant] = useState<BrandVariant>(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(`brand-update-days:${monthKey}`) || 'null');
      if (Array.isArray(cached?.dates)) return variantForToday(cached.dates, today);
    } catch {}
    return 'a';
  });

  useEffect(() => {
    let active = true;
    fetchMonthDates(year, month).then(dates => {
      if (active) setVariant(variantForToday(dates, today));
    }).catch(() => { /* Keep the deterministic A fallback when the public index is unavailable. */ });
    return () => { active = false; };
  }, [monthKey, month, today, year]);

  const value = useMemo(() => ({ variant }), [variant]);
  return <BrandDesignContext.Provider value={value}>{children}</BrandDesignContext.Provider>;
};

export function useBrandDesign(): BrandDesignValue {
  const value = useContext(BrandDesignContext);
  if (!value) throw new Error('useBrandDesign must be used within a BrandDesignProvider');
  return value;
}
